import { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, Plus, Trash2 } from 'lucide-react';
import { checkinsAPI, projectsAPI } from '../lib/api';
import { getUser } from '../lib/auth';
import { Avatar, Field, Modal, btnGhost, btnPrimary, fmtDateTime, inputCls } from '../components/ui';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function CheckinEditor({ checkin, roster, onClose, onSaved }) {
  const [name, setName] = useState(checkin?.name || 'Daily standup');
  const [questions, setQuestions] = useState(checkin?.questions || ['What did you do?', 'Any blockers?']);
  const [memberMids, setMemberMids] = useState(checkin?.member_mids || []);
  const [days, setDays] = useState(checkin?.schedule?.days || [0, 1, 2, 3, 4]);
  const [time, setTime] = useState(checkin?.schedule?.time || '09:00');

  const submit = async (e) => {
    e.preventDefault();
    const data = { name, questions: questions.filter((q) => q.trim()), member_mids: memberMids, schedule: { days, time }, active: true };
    if (checkin) await checkinsAPI.update(checkin.id, data);
    else await checkinsAPI.create(data);
    onSaved();
    onClose();
  };

  return (
    <Modal title={checkin ? 'Edit check-in' : 'New check-in'} onClose={onClose} wide>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} required className={inputCls} />
        </Field>
        <div>
          <span className="mb-1.5 block text-sm text-pm-muted">Questions</span>
          {questions.map((q, i) => (
            <div key={i} className="mb-1.5 flex gap-2">
              <input value={q} onChange={(e) => setQuestions((x) => x.map((y, j) => (j === i ? e.target.value : y)))} className={`${inputCls} py-1.5 text-sm`} />
              <button type="button" onClick={() => setQuestions((x) => x.filter((_, j) => j !== i))} className="rounded p-1 text-pm-faint hover:text-pm-danger">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => setQuestions((q) => [...q, ''])} className="inline-flex items-center gap-1 text-xs text-pm-muted hover:text-pm-text">
            <Plus size={12} /> Add question
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="mb-1.5 block text-sm text-pm-muted">Days</span>
            <div className="flex flex-wrap gap-1">
              {DAYS.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays((x) => (x.includes(i) ? x.filter((y) => y !== i) : [...x, i].sort()))}
                  className={`rounded-full border px-2 py-0.5 text-xs ${
                    days.includes(i) ? 'border-pm-accent text-pm-accent' : 'border-pm-border text-pm-muted'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <Field label="Time (UTC)">
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} />
          </Field>
        </div>
        <div>
          <span className="mb-1.5 block text-sm text-pm-muted">Participants</span>
          <div className="flex flex-wrap gap-1.5">
            {roster.map((m) => {
              const on = memberMids.includes(m.mid);
              return (
                <button
                  key={m.mid}
                  type="button"
                  onClick={() => setMemberMids((x) => (on ? x.filter((y) => y !== m.mid) : [...x, m.mid]))}
                  className={`rounded-full border px-2.5 py-1 text-xs ${on ? 'border-pm-accent text-pm-accent' : 'border-pm-border text-pm-muted'}`}
                >
                  {m.name}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button type="submit" disabled={!name.trim()} className={btnPrimary}>
            {checkin ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function RespondCard({ checkin, onDone }) {
  const [answers, setAnswers] = useState(checkin.questions.map(() => ''));
  const submit = async (e) => {
    e.preventDefault();
    await checkinsAPI.respond(checkin.id, answers);
    onDone();
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      {checkin.questions.map((q, i) => (
        <Field key={i} label={q}>
          <textarea value={answers[i]} onChange={(e) => setAnswers((a) => a.map((x, j) => (j === i ? e.target.value : x)))} rows={2} className={inputCls} />
        </Field>
      ))}
      <button type="submit" className={btnPrimary}>
        Submit today's check-in
      </button>
    </form>
  );
}

function Digest({ checkinId, date }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    checkinsAPI.digest(checkinId, date).then(({ data }) => setData(data));
  }, [checkinId, date]);
  if (!data) return null;
  return (
    <div className="space-y-4">
      {data.responses.map((r) => (
        <div key={r.member_mid} className="rounded-md border border-pm-border/60 bg-pm-bg/40 p-3">
          <div className="mb-2 flex items-center gap-2 text-xs">
            <Avatar name={r.member_name} size={20} />
            <b>{r.member_name}</b>
            <span className="text-pm-faint">{fmtDateTime(r.submitted_at)}</span>
          </div>
          {data.checkin.questions.map((q, i) => (
            <div key={i} className="mb-1.5">
              <div className="text-xs text-pm-faint">{q}</div>
              <div className="whitespace-pre-wrap text-sm">{r.answers[i] || '—'}</div>
            </div>
          ))}
        </div>
      ))}
      {data.responses.length === 0 && <p className="text-xs text-pm-faint">No responses for {data.date}.</p>}
    </div>
  );
}

export default function CheckinsPage() {
  const [checkins, setCheckins] = useState([]);
  const [roster, setRoster] = useState([]);
  const [editing, setEditing] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [date, setDate] = useState('');
  const user = getUser();
  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    const { data } = await checkinsAPI.list();
    setCheckins(data.checkins);
  }, []);

  useEffect(() => {
    load();
    projectsAPI.roster().then(({ data }) => setRoster(data.members));
  }, [load]);

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-lg font-semibold">Check-ins</h1>
        {isAdmin && (
          <button type="button" onClick={() => setEditing('new')} className={`${btnPrimary} inline-flex items-center gap-1.5`}>
            <Plus size={14} />
            New check-in
          </button>
        )}
      </div>
      <ul className="space-y-3">
        {checkins.map((c) => (
          <li key={c.id} className="rounded-lg border border-pm-border bg-pm-surface p-4">
            <div className="flex items-center gap-2">
              <ClipboardCheck size={15} className="text-pm-accent" />
              <button type="button" onClick={() => setOpenId(openId === c.id ? null : c.id)} className="text-sm font-semibold hover:text-pm-accent">
                {c.name}
              </button>
              <span className="text-xs text-pm-faint">
                {(c.schedule?.days || []).map((d) => DAYS[d]).join(' ')} at {c.schedule?.time} UTC · {c.member_mids.length} people
              </span>
              {c.responded_today && <span className="rounded-full bg-pm-raised px-2 py-0.5 text-[10px] text-pm-accent">responded today</span>}
              <span className="ml-auto" />
              {isAdmin && (
                <>
                  <button type="button" onClick={() => setEditing(c)} className="text-xs text-pm-muted hover:text-pm-text">
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm(`Delete "${c.name}" and its responses?`)) return;
                      await checkinsAPI.remove(c.id);
                      await load();
                    }}
                    className="rounded p-1 text-pm-faint hover:text-pm-danger"
                  >
                    <Trash2 size={13} />
                  </button>
                </>
              )}
            </div>
            {openId === c.id && (
              <div className="mt-4 border-t border-pm-border pt-4">
                {!c.responded_today && (
                  <div className="mb-5">
                    <RespondCard checkin={c} onDone={load} />
                  </div>
                )}
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-pm-faint">Digest</span>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`${inputCls} w-auto py-1 text-xs`} />
                </div>
                <Digest checkinId={c.id} date={date} />
              </div>
            )}
          </li>
        ))}
        {checkins.length === 0 && (
          <li className="rounded-lg border border-dashed border-pm-border py-14 text-center text-sm text-pm-muted">
            No check-ins yet{isAdmin ? ' — create the first standup prompt.' : '.'}
          </li>
        )}
      </ul>
      {editing && (
        <CheckinEditor checkin={editing === 'new' ? null : editing} roster={roster} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
}
